import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  HourglassTopRounded as PendingIcon,
  AssignmentTurnedIn as ConsentIcon,
  CloseRounded as CancelIcon,
} from "@mui/icons-material";
import ActionButton from "../../atoms/ActionButton";
import ConfirmDialog from "../../common/ConfirmDialog";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useResendSessionConsentMutation, useUpdatePracticeSessionMutation } from "../../../store/traditionalApi";
import SessionPaperConsentDrawer from "./SessionPaperConsentDrawer";
import { formatSentAgo } from "./practiceSessionFormat";

/**
 * "Onay bekliyor" seansın üst şeridi: ne zamandır beklediği + Yeniden gönder /
 * Kağıtla onayla / Seansı iptal et. Seans pending_consent değilse hiçbir şey çizmez.
 * Hasta sekmesindeki seans görünümü ve klinik geneli listenin drawer'ı ortak kullanır.
 *
 * @param {object} session
 * @param {boolean} canUpdate - sessions:update (yeniden gönder, iptal)
 * @param {boolean} canManageConsent - consents:manage (kağıtla onayla; ayrıca sessions:update ister)
 * @param {(session) => void} onChanged - işlem sonrası güncel seans
 */
export default function PendingConsentBanner({ session, token, clinicId, patientName, canUpdate, canManageConsent, onChanged, sx }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const [resend, { isLoading: resending }] = useResendSessionConsentMutation();
  const [updateSession, { isLoading: cancelling }] = useUpdatePracticeSessionMutation();
  const [paperOpen, setPaperOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  const sessionId = session?._id;
  useEffect(() => setResentAt(null), [sessionId]);

  if (!session || session.status !== "pending_consent") return null;

  const scope = clinicId ? { clinicId } : {};
  const sentAgo = formatSentAgo(resentAt || session.updatedAt || session.createdAt);

  const handleResend = async () => {
    try {
      const res = await resend({ token, ...scope, sessionId }).unwrap();
      setResentAt(new Date());
      showSuccess("Onay bildirimi yeniden gönderildi");
      if (res?.data?.session) onChanged?.(res.data.session);
    } catch (e) {
      showError(e?.data?.message || "Onay bildirimi gönderilemedi.");
    }
  };

  const handleCancel = async () => {
    try {
      const res = await updateSession({ token, ...scope, sessionId, status: "cancelled" }).unwrap();
      setConfirmCancel(false);
      showSuccess(`Seans iptal edildi; ${terms.patientGenLower} telefonundaki onay isteği kaldırıldı.`);
      if (res?.data?.session) onChanged?.(res.data.session);
    } catch (e) {
      showError(e?.data?.message || "Seans iptal edilemedi.");
    }
  };

  return (
    <>
      <Box
        role="status"
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 1.5,
          rowGap: 1,
          bgcolor: COLORS.warningLightest,
          borderBottom: `1px solid ${COLORS.warningLight}`,
          ...sx,
        }}
      >
        <PendingIcon sx={{ fontSize: 20, color: COLORS.warningDark }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 500, color: COLORS.warningDarker }}>
            {terms.patientGen} onayı bekleniyor{sentAgo ? ` · gönderildi ${sentAgo}` : ""}
          </Typography>
          <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate600 }}>
            {terms.patient} telefonundan onaylayınca seans kendiliğinden tamamlanır.
          </Typography>
        </Box>
        {canUpdate ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <ActionButton
              variant="send"
              compact
              tone="soft"
              palette={palette}
              label="Yeniden gönder"
              loading={resending}
              onClick={handleResend}
            />
            {canManageConsent ? (
              <ActionButton
                variant="confirm"
                compact
                tone="outline"
                palette={palette}
                icon={<ConsentIcon sx={{ fontSize: 15 }} />}
                label="Kağıtla onayla"
                onClick={() => setPaperOpen(true)}
              />
            ) : null}
            <ActionButton
              variant="delete"
              compact
              tone="soft"
              palette={palette}
              icon={<CancelIcon sx={{ fontSize: 15 }} />}
              label="Seansı iptal et"
              onClick={() => setConfirmCancel(true)}
            />
          </Box>
        ) : null}
      </Box>

      <SessionPaperConsentDrawer
        open={paperOpen}
        onClose={() => setPaperOpen(false)}
        token={token}
        clinicId={clinicId}
        session={session}
        patientName={patientName}
        onDone={(s) => s && onChanged?.(s)}
      />

      <ConfirmDialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleCancel}
        title="Seans iptal edilsin mi?"
        message={`Seans "İptal edildi" olarak işaretlenir ve ${terms.patientGenLower} telefonundaki onay isteği kaldırılır.`}
        confirmText="Seansı iptal et"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={cancelling}
        loadingText="İptal ediliyor..."
      />
    </>
  );
}
